import { useState } from 'react';

import { CheckOutlined, CloseOutlined, MailOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import { Card, Modal, Spin, Tabs } from 'antd';
import cn from 'classnames';
import { useParams } from 'react-router';

import { type ITest } from '@/api/endpoints/schools/schoolSelectionPanel';

import { ApproveParticipantSection } from './_components/ApproveParticipantSection/ApproveParticipantSection';
import { RejectParticipantSection } from './_components/RejectParticipantSection/RejectParticipantSection';
import styles from './ModerateParticipantModal.module.scss';
import { EModerateParticipantTabsKeys } from './types';
interface IProp {
  itemToOperate: ITest | null;
  isModalOpen: boolean;
  handleCancel: VoidFunction;
}

export const ModerateParticipantModal = ({ itemToOperate, isModalOpen, handleCancel }: IProp) => {
  const [activeTab, setActiveTab] = useState(EModerateParticipantTabsKeys.Approve);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const { id: schoolId } = useParams();

  if (itemToOperate) {
    const {
      id: participantId,
      user: { email, phone, full_name: fullName },
      approval_comment: approvalComment,
      rejection_reason: rejectionReason,
      block_user: isRejectedBySecurityService,
    } = itemToOperate;

    return (
      <Modal
        width={650}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        centered
      >
        <Spin spinning={isModalLoading}>
          <div className={styles.wrapper}>
            <div className={styles['header-wrapper']}>
              <p className={styles.title}>Модерация участника</p>
              <p className={styles.description}>
                Проверка документов и&nbsp;принятие решения. Участник успешно сдал тест. Одобрение означает зачисление
                в&nbsp;школу, отклонение&nbsp;&mdash; непрохождение&nbsp;СБ (причина скрыта от&nbsp;пользователя).
              </p>
            </div>
            <Card className={styles['user-info-wrapper']}>
              <div className={styles['user-info-value-wrapper']}>
                <UserOutlined />
                <p className={styles.name}>{fullName}</p>
              </div>
              <div className={styles['user-info-value-wrapper']}>
                <MailOutlined />
                <p className={styles.email}>{email}</p>
              </div>
              {phone && (
                <div className={styles['user-info-value-wrapper']}>
                  <PhoneOutlined rotate={90} />
                  <p className={styles.phone}>{phone}</p>
                </div>
              )}
            </Card>
            <Tabs
              onChange={(tab) => setActiveTab(tab as EModerateParticipantTabsKeys)}
              activeKey={activeTab}
              className={cn(styles.tabs, styles[`tabs-${activeTab}-active`])}
              items={[
                {
                  key: EModerateParticipantTabsKeys.Approve,
                  label: (
                    <p className={cn(styles['tab-label'], styles[`tab-label-${EModerateParticipantTabsKeys.Approve}`])}>
                      Одобрить
                    </p>
                  ),
                  children: (
                    <ApproveParticipantSection
                      handleCancel={handleCancel}
                      approvalComment={approvalComment}
                      participantId={participantId}
                      schoolId={Number(schoolId)}
                      setIsModalLoading={setIsModalLoading}
                    />
                  ),
                  icon: <CheckOutlined className={styles[`tab-icon-${EModerateParticipantTabsKeys.Approve}`]} />,
                },
                {
                  key: EModerateParticipantTabsKeys.Reject,
                  label: (
                    <p className={cn(styles['tab-label'], styles[`tab-label-${EModerateParticipantTabsKeys.Reject}`])}>
                      Отклонить
                    </p>
                  ),
                  children: (
                    <RejectParticipantSection
                      handleCancel={handleCancel}
                      rejectionReason={rejectionReason}
                      isRejectedBySecurityService={isRejectedBySecurityService}
                      schoolId={Number(schoolId)}
                      participantId={participantId}
                      setIsModalLoading={setIsModalLoading}
                    />
                  ),
                  icon: <CloseOutlined className={styles[`tab-icon-${EModerateParticipantTabsKeys.Reject}`]} />,
                },
              ]}
            />
          </div>
        </Spin>
      </Modal>
    );
  }
};
